import Appsignal, { type Breadcrumb, type Span } from '@appsignal/javascript';
import { plugin as windowEventsPlugin } from '@appsignal/plugin-window-events';

export let appsignal: Appsignal | undefined = undefined;

type AppsignalInitOptions = {
  key: string;
  revision?: string;
};

type ScalarValue = string | number | boolean;
type TagValue = ScalarValue | null | undefined;
type CompactTagMap = Record<string, string>;
type CompactBreadcrumbMetadata = Record<string, ScalarValue>;

type FrontendTags = Record<string, TagValue>;
type FrontendParams = Record<string, unknown>;
type FrontendBreadcrumb = {
  category: string;
  action: string;
  message?: string;
  metadata?: FrontendTags;
};

export type FrontendErrorContext = {
  action?: string;
  tags?: FrontendTags;
  params?: FrontendParams;
  breadcrumb?: FrontendBreadcrumb;
};

const SAFE_QUERY_PARAMS = [
  'mode',
  'view',
  'step_slug',
  'sidebar_mode',
  'item_id',
  'child_item_id',
  'grandchild_item_id',
  'testing_item_id',
] as const;

let breadcrumbListenersRegistered = false;
let networkBreadcrumbsRegistered = false;

const STATIC_ASSET_PATH_RE = /\.(?:avif|bmp|css|gif|ico|jpe?g|js|map|mjs|png|svg|ttf|webm|webmanifest|webp|woff2?)$/i;

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const compactTags = (tags: FrontendTags = {}): CompactTagMap => {
  const entries = Object.entries(tags)
    .filter(([, value]) => {
      if (value === null || value === undefined) return false;
      if (typeof value === 'string') return value.trim().length > 0;
      return typeof value === 'number' || typeof value === 'boolean';
    })
    .map(([key, value]) => [key, String(value)] as const);

  return Object.fromEntries(entries);
};

const compactBreadcrumbMetadata = (metadata: FrontendTags = {}): CompactBreadcrumbMetadata => {
  const entries = Object.entries(metadata)
    .filter(([, value]) => {
      if (value === null || value === undefined) return false;
      if (typeof value === 'string') return value.trim().length > 0;
      return typeof value === 'number' || typeof value === 'boolean';
    })
    .map(([key, value]) => [key, value as ScalarValue] as const);

  return Object.fromEntries(entries);
};

const toBreadcrumbScalar = (value: unknown): ScalarValue | undefined => {
  if (typeof value === 'string') return value.trim().length > 0 ? value : undefined;
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  return undefined;
};

const sanitizeParams = (value: unknown): unknown => {
  if (value === null || value === undefined) return undefined;
  if (typeof value === 'string') return value.trim().length > 0 ? value : undefined;
  if (typeof value === 'number' || typeof value === 'boolean') return value;

  if (Array.isArray(value)) {
    const nextValue = value.map(sanitizeParams).filter(item => item !== undefined);
    return nextValue.length > 0 ? nextValue : undefined;
  }

  if (isPlainObject(value)) {
    const nextEntries = Object.entries(value)
      .map(([key, nestedValue]) => [key, sanitizeParams(nestedValue)] as const)
      .filter(([, nestedValue]) => nestedValue !== undefined);

    return nextEntries.length > 0 ? Object.fromEntries(nextEntries) : undefined;
  }

  return String(value);
};

const compactParams = (params: FrontendParams = {}) => (sanitizeParams(params) as FrontendParams | undefined) ?? {};

const getSafeQueryParams = () => {
  const searchParams = new URLSearchParams(window.location.search);

  return SAFE_QUERY_PARAMS.reduce<Record<string, string | undefined>>((acc, key) => {
    const value = searchParams.get(key);
    acc[key] = value && value.trim().length > 0 ? value : undefined;
    return acc;
  }, {});
};

const getRouteName = (pathname: string) => {
  const segments = pathname.split('/').filter(Boolean);

  if (segments[0] === 'flows') {
    if (segments[3] === 'edit') return 'flow-edit';
    if (segments.length >= 4) return 'flow-item';
    if (segments.length >= 3) return 'flow-index';
    if (segments.length >= 2) return 'flow-org';
    return 'flows';
  }

  if (segments[0]) return segments[0];
  return 'home';
};

const getDefaultContext = (): Pick<FrontendErrorContext, 'action' | 'tags' | 'params'> => {
  const { pathname, hash } = window.location;
  const segments = pathname.split('/').filter(Boolean);
  const safeQuery = getSafeQueryParams();
  const phxRoot = document.querySelector('[data-phx-main]') as HTMLElement | null;
  const routeName = getRouteName(pathname);
  const pathItemId = segments[0] === 'flows' && segments[3] && segments[3] !== 'edit' ? segments[3] : undefined;
  let referrerHost: string | undefined;

  if (document.referrer) {
    try {
      referrerHost = new URL(document.referrer).host;
    } catch (_error) {
      referrerHost = undefined;
    }
  }

  return {
    action: `Frontend#${routeName}`,
    tags: compactTags({
      route_name: routeName,
      org_slug: segments[0] === 'flows' ? segments[1] : undefined,
      flow_slug: segments[0] === 'flows' ? segments[2] : undefined,
      mode: safeQuery.mode,
      view_slug: safeQuery.view,
      step_slug: safeQuery.step_slug,
      sidebar_mode: safeQuery.sidebar_mode,
      has_search_query: new URLSearchParams(window.location.search).has('search'),
    }),
    params: compactParams({
      pathname,
      hash,
      item_id: pathItemId ?? safeQuery.item_id,
      child_item_id: safeQuery.child_item_id,
      grandchild_item_id: safeQuery.grandchild_item_id,
      testing_item_id: safeQuery.testing_item_id,
      mode: safeQuery.mode,
      view_slug: safeQuery.view,
      step_slug: safeQuery.step_slug,
      sidebar_mode: safeQuery.sidebar_mode,
      phx_main_id: phxRoot?.id,
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight,
      visibility_state: document.visibilityState,
      referrer_host: referrerHost,
      has_search_query: new URLSearchParams(window.location.search).has('search'),
    }),
  };
};

export const decorateSpan = (span: Span, context: FrontendErrorContext = {}) => {
  const { action, tags, params } = context;

  if (action) span.setAction(action);
  if (tags) span.setTags(compactTags(tags));
  if (params) span.setParams(compactParams(params));

  return span;
};

export const addBreadcrumb = (breadcrumb: FrontendBreadcrumb) => {
  if (!appsignal) return;

  const metadata = compactBreadcrumbMetadata(breadcrumb.metadata ?? {});
  const nextBreadcrumb: Omit<Breadcrumb, 'timestamp'> = {
    category: breadcrumb.category,
    action: breadcrumb.action,
    ...(breadcrumb.message ? { message: breadcrumb.message } : {}),
    ...(Object.keys(metadata).length > 0 ? { metadata } : {}),
  };

  appsignal.addBreadcrumb(nextBreadcrumb);
};

const sanitizeNavigationTarget = (value: unknown) => {
  if (typeof value !== 'string' || value.trim().length === 0) return undefined;

  try {
    const url = new URL(value, window.location.origin);
    return url.pathname;
  } catch (_error) {
    return undefined;
  }
};

const nowInMs = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());

const durationInMs = (startedAt: number) => Math.max(0, Math.round(nowInMs() - startedAt));

type NetworkTarget = {
  host: string | undefined;
  pathname: string;
  sameOrigin: boolean;
  target: string;
};

const getNetworkTarget = (rawUrl: string | URL): NetworkTarget | null => {
  try {
    const url = rawUrl instanceof URL ? rawUrl : new URL(rawUrl, window.location.origin);
    const sameOrigin = url.origin === window.location.origin;
    const pathname = url.pathname || '/';

    return {
      host: sameOrigin ? undefined : url.host,
      pathname,
      sameOrigin,
      target: sameOrigin ? pathname : `${url.host}${pathname}`,
    };
  } catch (_error) {
    return null;
  }
};

const shouldTrackNetworkTarget = (target: NetworkTarget | null, method: string) => {
  if (!target) return false;
  if (method === 'OPTIONS') return false;
  if (STATIC_ASSET_PATH_RE.test(target.pathname)) return false;
  if (target.host?.includes('appsignal')) return false;
  return true;
};

const addNetworkBreadcrumb = ({
  kind,
  method,
  target,
  status,
  durationMs,
  outcome,
  host,
  sameOrigin,
  errorName,
}: {
  kind: 'fetch' | 'xhr';
  method: string;
  target: string;
  status: number | undefined;
  durationMs: number;
  outcome: 'completed' | 'error' | 'aborted' | 'timeout';
  host: string | undefined;
  sameOrigin: boolean;
  errorName: string | undefined;
}) => {
  addBreadcrumb({
    category: 'request',
    action: kind,
    message: `${method} ${target}`,
    metadata: {
      duration_ms: durationMs,
      error_name: errorName,
      host,
      outcome,
      same_origin: sameOrigin,
      status,
    },
  });
};

const registerNetworkBreadcrumbs = () => {
  if (networkBreadcrumbsRegistered) return;
  networkBreadcrumbsRegistered = true;

  if (typeof window.fetch === 'function') {
    const originalFetch = window.fetch.bind(window);

    window.fetch = async (...args) => {
      const [input, init] = args;
      const request = input instanceof Request ? input : undefined;
      const method = (request?.method || init?.method || 'GET').toUpperCase();
      const rawUrl = request?.url || (input instanceof URL ? input : typeof input === 'string' ? input : undefined);
      const target = rawUrl ? getNetworkTarget(rawUrl) : null;
      const startedAt = nowInMs();

      try {
        const response = await originalFetch(...args);
        if (target && shouldTrackNetworkTarget(target, method)) {
          addNetworkBreadcrumb({
            kind: 'fetch',
            method,
            target: target.target,
            status: response.status,
            durationMs: durationInMs(startedAt),
            outcome: 'completed',
            host: target.host,
            sameOrigin: target.sameOrigin,
            errorName: undefined,
          });
        }
        return response;
      } catch (error) {
        if (target && shouldTrackNetworkTarget(target, method)) {
          addNetworkBreadcrumb({
            kind: 'fetch',
            method,
            target: target.target,
            status: undefined,
            durationMs: durationInMs(startedAt),
            outcome: 'error',
            host: target.host,
            sameOrigin: target.sameOrigin,
            errorName: error instanceof Error ? error.name : undefined,
          });
        }
        throw error;
      }
    };
  }

  if (typeof XMLHttpRequest !== 'undefined') {
    type TrackedXHR = XMLHttpRequest & {
      __appsignalNetworkRequest?: {
        method: string;
        target: NetworkTarget;
      };
    };

    const originalOpen = XMLHttpRequest.prototype.open as (
      this: XMLHttpRequest,
      method: string,
      url: string | URL,
      async?: boolean,
      username?: string | null,
      password?: string | null,
    ) => void;
    const originalSend = XMLHttpRequest.prototype.send as (
      this: XMLHttpRequest,
      body?: Document | XMLHttpRequestBodyInit | null,
    ) => void;

    XMLHttpRequest.prototype.open = function (
      method: string,
      url: string | URL,
      async?: boolean,
      username?: string | null,
      password?: string | null,
    ) {
      const normalizedMethod = typeof method === 'string' ? method.toUpperCase() : 'GET';
      const target = typeof url === 'string' || url instanceof URL ? getNetworkTarget(url) : null;

      if (shouldTrackNetworkTarget(target, normalizedMethod) && target) {
        (this as TrackedXHR).__appsignalNetworkRequest = {
          method: normalizedMethod,
          target,
        };
      } else {
        delete (this as TrackedXHR).__appsignalNetworkRequest;
      }

      return originalOpen.call(this, method, url, async, username, password);
    };

    XMLHttpRequest.prototype.send = function (body?: Document | XMLHttpRequestBodyInit | null) {
      const xhr = this as TrackedXHR;
      const requestMeta = xhr.__appsignalNetworkRequest;

      if (!requestMeta) {
        return originalSend.call(xhr, body);
      }

      const startedAt = nowInMs();
      let finished = false;

      const finalize = (outcome: 'completed' | 'error' | 'aborted' | 'timeout', errorName?: string) => {
        if (finished) return;
        finished = true;

        addNetworkBreadcrumb({
          kind: 'xhr',
          method: requestMeta.method,
          target: requestMeta.target.target,
          status: xhr.status || undefined,
          durationMs: durationInMs(startedAt),
          outcome,
          host: requestMeta.target.host,
          sameOrigin: requestMeta.target.sameOrigin,
          errorName,
        });
      };

      xhr.addEventListener('loadend', () => finalize('completed'), { once: true });
      xhr.addEventListener('error', () => finalize('error'), { once: true });
      xhr.addEventListener('abort', () => finalize('aborted'), { once: true });
      xhr.addEventListener('timeout', () => finalize('timeout'), { once: true });

      return originalSend.call(xhr, body);
    };
  }
};

const registerBreadcrumbListeners = () => {
  if (breadcrumbListenersRegistered) return;
  breadcrumbListenersRegistered = true;

  window.addEventListener('phx:page-loading-start', event => {
    const detail = (event as CustomEvent<Record<string, unknown>>).detail || {};
    const message = typeof detail.kind === 'string' ? detail.kind : undefined;

    addBreadcrumb({
      category: 'navigation',
      action: 'page-loading-start',
      ...(message ? { message } : {}),
      metadata: {
        kind: toBreadcrumbScalar(detail.kind),
        target: sanitizeNavigationTarget(detail.to ?? detail.href ?? detail.url),
      },
    });
  });

  window.addEventListener('phx:page-loading-stop', event => {
    const detail = (event as CustomEvent<Record<string, unknown>>).detail || {};
    const message = typeof detail.kind === 'string' ? detail.kind : undefined;

    addBreadcrumb({
      category: 'navigation',
      action: 'page-loading-stop',
      ...(message ? { message } : {}),
      metadata: {
        kind: toBreadcrumbScalar(detail.kind),
        target: sanitizeNavigationTarget(detail.to ?? detail.href ?? detail.url),
      },
    });
  });

  window.addEventListener('phx:focusInput', event => {
    const detail = (event as CustomEvent<Record<string, unknown>>).detail || {};
    const message = typeof detail.name === 'string' ? detail.name : undefined;

    addBreadcrumb({
      category: 'interaction',
      action: 'focus-input',
      ...(message ? { message } : {}),
      metadata: {
        placement: toBreadcrumbScalar(detail.placement),
        open_slash: detail.open_slash === true || detail.openSlash === true,
      },
    });
  });

  window.addEventListener('phx:copy-to-clipboard', event => {
    const detail = (event as CustomEvent<Record<string, unknown>>).detail || {};
    const clipboard = typeof detail.clipboard === 'string' ? detail.clipboard : '';

    addBreadcrumb({
      category: 'interaction',
      action: 'copy-to-clipboard',
      metadata: {
        clipboard_length: clipboard.length,
      },
    });
  });

  window.addEventListener('phx:open_url', event => {
    const detail = (event as CustomEvent<Record<string, unknown>>).detail || {};

    if (typeof detail.url !== 'string' || detail.url.trim().length === 0) return;

    try {
      const url = new URL(detail.url, window.location.origin);
      addBreadcrumb({
        category: 'navigation',
        action: 'open-url',
        metadata: {
          same_origin: url.origin === window.location.origin,
          target_host: url.host,
          target_path: url.origin === window.location.origin ? url.pathname : undefined,
        },
      });
    } catch (_error) {
      addBreadcrumb({
        category: 'navigation',
        action: 'open-url',
      });
    }
  });

  window.addEventListener('popstate', () => {
    addBreadcrumb({
      category: 'navigation',
      action: 'history-popstate',
      metadata: {
        pathname: window.location.pathname,
      },
    });
  });
};

export const initialize = ({ key, revision }: AppsignalInitOptions) => {
  const trimmedRevision = revision?.trim();
  const options = {
    key,
    ...(trimmedRevision ? { revision: trimmedRevision } : {}),
  };

  appsignal = new Appsignal(options);

  appsignal.addDecorator(span => decorateSpan(span, getDefaultContext()));
  appsignal.use(windowEventsPlugin());
  registerBreadcrumbListeners();
  registerNetworkBreadcrumbs();
};
